/**
 * La cola de subida. El corazón del modo offline.
 *
 * Un clip entra aquí en cuanto se para de grabar, y sale cuando el servidor
 * confirma su sha256. Entre esas dos cosas puede pasar cualquier cosa: perder
 * la cobertura a mitad de un trozo, que la empleada cierre la app, que iOS
 * mate la pestaña por memoria, que el móvil se apague. La cola está escrita
 * para que ninguna de esas cosas pierda una prueba.
 *
 * CINCO DECISIONES QUE LA EXPLICAN
 *
 * 1. **Se sube clip a clip mientras se graba el siguiente** (decisión D3). No
 *    se espera al final de la limpieza. En un iPhone no hay subida en segundo
 *    plano, así que la única ventana fiable es mientras la empleada tiene la
 *    app abierta grabando: hay que aprovecharla.
 *
 * 2. **Una subida a la vez.** No es simplicidad: la línea de la oficina es el
 *    cuello de botella (180 MB por limpieza) y dos subidas en paralelo por el
 *    mismo tubo tardan lo mismo en total y hacen que ninguna acabe. Con una,
 *    cada clip que termina es un clip a salvo.
 *
 * 3. **El servidor manda sobre qué falta.** Cada `/init` y cada trozo
 *    devuelven la lista de `pendientes`. La cola no lleva su propia
 *    contabilidad de lo enviado: la pregunta. Así una reanudación tras doce
 *    horas no reenvía 180 MB por creerse una lista local desactualizada.
 *
 * 4. **El blob se borra cuando llega el `asset` con su hash.** Antes, no. Ni
 *    al mandar el último trozo ni al recibir un 201 sin cuerpo.
 *
 * 5. **Un 4xx no se reintenta.** El servidor ha entendido y ha dicho no;
 *    insistir gasta batería y datos. Se marca fallida, se explica, y la
 *    empleada decide desde la pantalla 08.
 *
 * 6. **Un servidor que falla siempre tampoco se reintenta para siempre.** Sin
 *    red se insiste sin límite —eso es el producto—, pero un 5xx que se repite
 *    es un fallo que no se va a arreglar solo esperando, y una cola que nunca
 *    baja de cero es peor que un error: la pantalla dice "deja la app abierta
 *    hasta que acabe de subir" sobre algo que no va a acabar. Tras
 *    `MAX_INTENTOS_SERVIDOR` se marca fallida y se ve en la pantalla 08, con
 *    su botón de reintentar por si el servidor ya está de vuelta.
 */

import { ErrorApi, ErrorDeRed, sha256, ulid, type Cliente, type Posicion } from '@haus/core';

import {
    borrarClip,
    db,
    espacio,
    guardarClip,
    leerClip,
    guardarLimpieza,
    incidenciasPendientes,
    leerAjustes,
    leerLimpieza,
    type EnCola,
    type EstadoCola,
} from './db.js';

/** Backoff del handoff: 2 s, 8 s, 30 s, 2 min, 10 min. */
const ESPERAS = [2_000, 8_000, 30_000, 120_000, 600_000];

/**
 * Cuántas veces se insiste ante un error DEL SERVIDOR antes de aparcar el clip.
 *
 * Con el backoff de arriba son unos 23 minutos. No se aplica a `sin_red`: un
 * piso sin cobertura no es un fallo, es el caso normal, y ahí se espera lo que
 * haga falta.
 */
const MAX_INTENTOS_SERVIDOR = 6;

export interface ResumenCola {
    total: number;
    pendientes: number;
    subiendo: number;
    fallidas: number;
    hechas: number;
    bytes_pendientes: number;
    /** El item en curso, para la barra de progreso. */
    actual: {
        id: string;
        apartamento: string;
        estancia: string;
        bytes: number;
        enviados: number;
        pct: number;
    } | null
    trabajando: boolean;
    motivo_parada: 'sin_red' | 'esperando_wifi' | 'nada_que_hacer' | 'reintentando' | null;
    almacenamiento: { usado: number; cuota: number | null; pct: number };
}

type Oyente = (r: ResumenCola) => void;

export interface NuevoEnCola {
    cleaning_id: string;
    cleaning_room_id: string | null;
    room_punto_id: string | null;
    apartamento: string;
    estancia: string;
    tipo: EnCola['tipo'];
    blob: Blob;
    capturado_at: string;
    duracion_ms: number | null;
    ancho: number | null;
    alto: number | null;
    calidad: EnCola['calidad'];
    gps: Posicion | null;
}

export class Cola {
    private oyentes = new Set<Oyente>();
    private trabajando = false;
    private parada: ResumenCola['motivo_parada'] = null;
    private actual: ResumenCola['actual'] = null;
    private wakeLock: WakeLockSentinel | null = null;
    private temporizador: number | null = null;
    private abortar: AbortController | null = null;

    constructor(private readonly cliente: Cliente) {
        // Al volver la red, arrancar sin esperar al siguiente reintento: es el
        // momento en el que la empleada sale del portal y coge cobertura.
        addEventListener('online', () => {
            void this.arrancar('volvio la red');
        });
        addEventListener('offline', () => {
            this.abortar?.abort();
        });

        // Al volver a primer plano, lo mismo. En iOS es LA señal que importa:
        // mientras la app estuvo en segundo plano no se subió nada.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                void this.arrancar('vuelve a primer plano');
            }
        });
    }

    // --- API pública ----------------------------------------------------

    escuchar(o: Oyente): () => void {
        this.oyentes.add(o);
        void this.avisar();

        return () => this.oyentes.delete(o);
    }

    /**
     * Mete un clip o una foto en la cola. Calcula el sha256 aquí, antes de
     * guardar: si el hash no se pudiera calcular (sin HTTPS no hay
     * crypto.subtle) es mejor saberlo ahora que con el clip ya en la cola.
     */
    async anadir(n: NuevoEnCola): Promise<string> {
        const id = ulid();
        const hash = await sha256(n.blob);
        const ajustes = await leerAjustes();

        const item: EnCola = {
            id,
            cleaning_id: n.cleaning_id,
            cleaning_room_id: n.cleaning_room_id,
            room_punto_id: n.room_punto_id,
            apartamento: n.apartamento,
            estancia: n.estancia,
            tipo: n.tipo,
            mime: n.blob.type !== '' ? n.blob.type : 'application/octet-stream',
            bytes: n.blob.size,
            sha256: hash,
            tiene_clip: true,
            capturado_at: n.capturado_at,
            duracion_ms: n.duracion_ms,
            ancho: n.ancho,
            alto: n.alto,
            calidad: n.calidad ?? ajustes.calidad,
            gps: n.gps,
            estado: 'pendiente',
            intentos: 0,
            reintentar_at: 0,
            upload_id: null,
            chunk_bytes: 5 * 1024 * 1024,
            enviados: [],
            ultimo_error: null,
            creado_at: Date.now(),
        };

        // El fichero primero: si se guardara el metadato antes y el fichero
        // fallara, quedaria una fila diciendo que hay un clip que no existe.
        await guardarClip(id, n.blob);
        await (await db()).put('cola', item);
        void this.arrancar('clip nuevo');

        return id;
    }

    async items(): Promise<EnCola[]> {
        const todos = await (await db()).getAll('cola');

        return todos.sort((a, b) => a.creado_at - b.creado_at);
    }

    /** "Reintentar ahora" de la pantalla 08. */
    async reintentar(id?: string): Promise<void> {
        const base = await db();
        const items = id === undefined ? await base.getAll('cola') : [await base.get('cola', id)];

        for (const item of items) {
            if (item === undefined || item.estado === 'hecha') {
                continue;
            }
            await base.put('cola', {
                ...item,
                estado: 'pendiente',
                intentos: 0,
                reintentar_at: 0,
                ultimo_error: null,
            });
        }

        void this.arrancar('reintento manual');
    }

    async resumen(): Promise<ResumenCola> {
        const items = await this.items();
        const cuenta = (e: EstadoCola): number => items.filter((i) => i.estado === e).length;

        return {
            total: items.length,
            pendientes: cuenta('pendiente'),
            subiendo: cuenta('subiendo'),
            fallidas: cuenta('fallida'),
            hechas: cuenta('hecha'),
            bytes_pendientes: items
                .filter((i) => i.estado !== 'hecha')
                .reduce((t, i) => t + i.bytes, 0),
            actual: this.actual,
            trabajando: this.trabajando,
            motivo_parada: this.parada,
            almacenamiento: await espacio(),
        };
    }

    // --- El bucle -------------------------------------------------------

    /**
     * Arranca el bucle si no está ya en marcha.
     *
     * Reentrante a propósito: lo llaman el evento `online`, el cambio de
     * visibilidad, cada clip nuevo y el reintento manual. Con la bandera
     * `trabajando` solo hay un bucle a la vez.
     */
    async arrancar(motivo = 'manual'): Promise<void> {
        if (this.trabajando) {
            return;
        }

        this.trabajando = true;
        console.info('[cola] arranca:', motivo);

        try {
            await this.pedirWakeLock();
            await this.bucle();
        } finally {
            this.trabajando = false;
            this.actual = null;
            await this.soltarWakeLock();
            await this.avisar();
        }
    }

    private async bucle(): Promise<void> {
        for (;;) {
            if (!navigator.onLine) {
                this.parada = 'sin_red';

                return;
            }

            if (await this.esperandoWifi()) {
                this.parada = 'esperando_wifi';

                return;
            }

            await this.sincronizarIncidencias();

            const item = await this.siguiente();
            if (item === null) {
                const proximo = await this.proximoReintento();
                if (proximo !== null) {
                    // Hay trabajo, pero toca esperar. Se programa y se sale:
                    // dejar el bucle girando gasta batería sin ganar nada.
                    this.parada = 'reintentando';
                    this.programar(proximo - Date.now());
                } else {
                    this.parada = 'nada_que_hacer';
                }

                return;
            }

            this.parada = null;
            const resultado = await this.subir(item);
            await this.avisar();

            if (resultado === 'parar') {
                // Sin red: el siguiente fallaría igual, así que se sale.
                //
                // PERO SE PROGRAMA LA VUELTA. Antes se salía confiando solo en
                // el evento `online`, y eso deja un agujero por el que se cae
                // el día entero: si la petición se cae sin que el dispositivo
                // llegue a marcarse offline —una conmutación de WiFi a datos,
                // un corte de un segundo, el proxy de un hotel— no hay evento
                // `online` que llegue nunca, porque nunca hubo `offline`. La
                // cola se quedaba parada con la app abierta y la pantalla
                // diciendo "deja la app abierta hasta que acabe de subir".
                // Lo encontró la prueba de WebKit con un `fetch` que falló
                // suelto: dos fotos subidas, tres esperando para siempre.
                this.parada = 'sin_red';
                const proximo = await this.proximoReintento();
                this.programar(proximo === null ? 30_000 : proximo - Date.now());

                return;
            }
        }
    }

    private async siguiente(): Promise<EnCola | null> {
        const ahora = Date.now();
        const items = await this.items();

        return (
            items.find(
                (i) =>
                    (i.estado === 'pendiente' || i.estado === 'subiendo') && i.reintentar_at <= ahora
            ) ?? null
        );
    }

    private async proximoReintento(): Promise<number | null> {
        const items = await this.items();
        const esperando = items
            .filter((i) => i.estado !== 'hecha' && i.reintentar_at > Date.now())
            .map((i) => i.reintentar_at);

        return esperando.length === 0 ? null : Math.min(...esperando);
    }

    private programar(ms: number): void {
        if (this.temporizador !== null) {
            clearTimeout(this.temporizador);
        }
        this.temporizador = setTimeout(() => {
            this.temporizador = null;
            void this.arrancar('toca reintentar');
        }, Math.max(500, ms)) as unknown as number;
    }

    // --- Subir un item --------------------------------------------------

    private async subir(item: EnCola): Promise<'seguir' | 'parar'> {
        const base = await db();

        const clip = await leerClip(item.id);
        if (clip === null) {
            // Sin blob no hay nada que subir. Pasa si el navegador tiró la
            // base local: se marca y se ve en la pantalla 08, porque esa
            // prueba se ha perdido y hay que decirlo, no esconderlo.
            await base.put('cola', {
                ...item,
                estado: 'fallida',
                tiene_clip: false,
                ultimo_error: 'el fichero ya no está en el móvil',
            });

            return 'seguir';
        }

        this.abortar = new AbortController();
        this.actual = {
            id: item.id,
            apartamento: item.apartamento,
            estancia: item.estancia,
            bytes: item.bytes,
            enviados: 0,
            pct: 0,
        };
        await this.avisar();

        try {
            // La limpieza tiene que existir en el servidor antes que su
            // primer clip. Se creó sin cobertura, así que puede ser la
            // primera vez que se manda.
            await this.asegurarLimpieza(item.cleaning_id);

            const inicio = await this.cliente.iniciarSubida({
                asset_id: item.id,
                cleaning_id: item.cleaning_id,
                cleaning_room_id: item.cleaning_room_id,
                room_punto_id: item.room_punto_id,
                tipo: item.tipo,
                mime: item.mime,
                bytes: item.bytes,
                sha256: item.sha256,
                capturado_at: item.capturado_at,
                duracion_ms: item.duracion_ms,
                ancho: item.ancho,
                alto: item.alto,
                calidad: item.calidad,
                gps_lat: item.gps?.lat ?? null,
                gps_lng: item.gps?.lng ?? null,
                gps_acc: item.gps?.acc ?? null,
            });

            if (inicio.estado === 'ya_confirmado') {
                // El servidor ya lo tenía: la respuesta anterior se perdió en
                // el camino. Es el caso normal cuando se corta la red justo
                // después de confirmar, y por eso /init es idempotente.
                await this.terminar(item, 'ya estaba confirmado');

                return 'seguir';
            }

            let pendientes = inicio.pendientes;
            const chunk = inicio.chunk_bytes;
            let enviados = inicio.bytes_recibidos;

            await base.put('cola', {
                ...item,
                estado: 'subiendo',
                upload_id: inicio.upload_id,
                chunk_bytes: chunk,
                enviados: [],
            });

            for (const n of pendientes) {
                if (!navigator.onLine) {
                    throw new ErrorApi(0, 'sin_red');
                }

                const desde = n * chunk;
                const hasta = Math.min(clip.size, desde + chunk);
                const estado = await this.cliente.subirTrozo(
                    inicio.upload_id,
                    n,
                    clip.slice(desde, hasta),
                    this.abortar.signal
                );

                // El servidor dice qué falta; la cola no lo deduce.
                pendientes = estado.pendientes;
                enviados = estado.bytes_recibidos;
                this.actual = {
                    ...this.actual,
                    enviados,
                    pct: Math.floor((enviados / Math.max(1, item.bytes)) * 100),
                };
                await this.avisar();
            }

            const confirmado = await this.cliente.completarSubida(inicio.upload_id, item.sha256);

            // AQUÍ, y solo aquí, se puede soltar el blob: el servidor ha
            // recalculado el hash sobre el fichero ensamblado y coincide.
            if (confirmado.asset.sha256 !== item.sha256) {
                throw new ErrorApi(500, 'sha256_no_coincide', {
                    esperado: item.sha256,
                    recibido: confirmado.asset.sha256,
                });
            }

            await this.terminar(item, confirmado.asset.path);
        } catch (e) {
            try {
                return await this.fallo(item, e);
            } catch (peor) {
                // Anotar el fallo también ha fallado. Si esto se deja subir,
                // se lleva por delante el bucle entero y la cola muere en
                // silencio con la app abierta. Se avisa y se sigue con el
                // siguiente: el clip se queda como estaba y el próximo arranque
                // lo recoge, porque `siguiente()` también mira los `subiendo`.
                console.error('[cola] no se pudo ni apuntar el fallo', item.id, peor);

                return 'seguir';
            }
        } finally {
            this.abortar = null;
        }

        return 'seguir';
    }

    private async terminar(item: EnCola, detalle: string): Promise<void> {
        console.info('[cola] confirmado', item.id, detalle);

        const base = await db();
        await borrarClip(item.id);
        await base.put('cola', {
            ...item,
            estado: 'hecha',
            tiene_clip: false,
            ultimo_error: null,
        });
    }

    private async fallo(item: EnCola, e: unknown): Promise<'seguir' | 'parar'> {
        const base = await db();
        const intentos = item.intentos + 1;

        const esApi = e instanceof ErrorApi;
        const codigo = esApi ? e.codigo : 'error_desconocido';
        const reintentable = esApi ? e.reintentable : true;

        // La causa de debajo, no solo el codigo. Un `sin_red` puede ser el
        // portal sin cobertura o una peticion abortada por otra razon, y
        // depurando esto desde el movil de una empleada la diferencia es todo.
        const causa = e instanceof ErrorDeRed ? String((e.causa as Error)?.message ?? e.causa) : '';
        console.warn('[cola] fallo', item.id, codigo, `intento ${intentos}`, causa);

        if (!reintentable) {
            // El servidor ha entendido y ha dicho no. Insistir no lo va a
            // cambiar, y la empleada tiene que verlo en la pantalla 08.
            await base.put('cola', {
                ...item,
                estado: 'fallida',
                intentos,
                ultimo_error: codigo,
            });

            return 'seguir';
        }

        // Sin red se espera indefinidamente; un servidor que devuelve 500 una
        // y otra vez, no. Aparcarlo lo hace visible en lugar de dejar la cola
        // girando en silencio hasta que se acabe la batería.
        if (codigo !== 'sin_red' && intentos >= MAX_INTENTOS_SERVIDOR) {
            console.warn('[cola] aparcada tras', intentos, 'intentos', item.id, codigo);
            await base.put('cola', {
                ...item,
                estado: 'fallida',
                intentos,
                ultimo_error: codigo,
            });

            return 'seguir';
        }

        const espera = ESPERAS[Math.min(intentos - 1, ESPERAS.length - 1)] ?? 600_000;

        // Se relee de la base en lugar de reusar el `item` de entrada: cuando
        // esto se escribió, `{...item}` devolvía el `upload_id` a null y se
        // perdía la sesión reanudable que `subir()` acababa de guardar. La
        // subida seguía funcionando porque `/init` es idempotente y el
        // servidor devuelve la sesión que ya había, pero era suerte, no diseño.
        const guardado = (await base.get('cola', item.id)) ?? item;
        await base.put('cola', {
            ...guardado,
            estado: 'pendiente',
            intentos,
            reintentar_at: Date.now() + espera,
            ultimo_error: codigo,
        });

        // Pararse del todo solo si el dispositivo está realmente sin red. Una
        // petición que se cae con `navigator.onLine` en true es un fallo
        // suelto, no un portal sin cobertura: el siguiente clip puede subir
        // perfectamente y frenar la cola entera por eso es tirar el día.
        return codigo === 'sin_red' && !navigator.onLine ? 'parar' : 'seguir';
    }

    /**
     * Crea la limpieza en el servidor si todavía no existe.
     *
     * Es idempotente por el ULID que generó el móvil, así que se puede llamar
     * tantas veces como haga falta. Y tiene que llamarse antes del primer
     * clip: sin limpieza, `/uploads/init` no tiene dónde colgarlo.
     */
    private async asegurarLimpieza(id: string): Promise<void> {
        const local = await leerLimpieza(id);
        if (local === null || local.sincronizada) {
            return;
        }

        await this.cliente.crearLimpieza({
            id: local.id,
            property_id: local.property_id,
            assignment_id: local.assignment_id,
            started_at: local.started_at,
            modo_captura: local.modo,
            app_version: __VERSION__,
            gps: local.gps,
            estancias: local.estancias.map((e) => ({ id: e.id, room_id: e.room_id })),
        });

        await guardarLimpieza({ ...local, sincronizada: true });
    }

    /**
     * Manda las incidencias pendientes.
     *
     * Van con la cola y no con la pantalla: la empleada escribe "la mampara
     * está rajada" en un piso sin cobertura y espera que llegue, no que se
     * pierda por haber pulsado guardar sin red.
     */
    private async sincronizarIncidencias(): Promise<void> {
        const pendientes = await incidenciasPendientes();
        if (pendientes.length === 0) {
            return;
        }

        const base = await db();
        for (const i of pendientes) {
            try {
                await this.cliente.crearIncidencia({
                    id: i.id,
                    cleaning_id: i.cleaning_id,
                    cleaning_room_id: i.cleaning_room_id,
                    texto: i.texto,
                    gravedad: i.gravedad,
                });
                await base.put('incidencias', { ...i, sincronizada: true });
            } catch (e) {
                if (e instanceof ErrorApi && !e.reintentable) {
                    // Un 4xx aquí es un error de datos: se marca sincronizada
                    // para no bloquear la cola con algo que nunca entrará, y
                    // queda el texto local para poder verlo.
                    console.error('[cola] incidencia rechazada', i.id, e.codigo);
                    await base.put('incidencias', { ...i, sincronizada: true });
                    continue;
                }

                return; // sin red: se reintenta en la siguiente pasada
            }
        }
    }

    // --- WiFi, pantalla y avisos ----------------------------------------

    /**
     * ¿Hay que esperar a tener WiFi?
     *
     * `navigator.connection` solo existe en Chrome. En iOS **no hay forma de
     * saber si el móvil está en WiFi o en datos**, así que con "solo WiFi"
     * activo en un iPhone la cola subiría igual, y eso sería incumplir el
     * ajuste sin decirlo. Se resuelve al revés: si no se puede saber, se sube.
     * La alternativa —no subir nunca en iOS con esa opción— dejaría las
     * pruebas en el móvil sin que nadie entienda por qué.
     *
     * La pantalla 08 lo explica cuando el ajuste está activo y no se puede
     * comprobar. Prometer menos y cumplirlo.
     */
    private async esperandoWifi(): Promise<boolean> {
        const ajustes = await leerAjustes();
        if (!ajustes.solo_wifi) {
            return false;
        }

        const con = (navigator as { connection?: { type?: string } }).connection;
        if (con?.type === undefined) {
            return false; // no se puede saber: se sube
        }

        return con.type !== 'wifi' && con.type !== 'ethernet';
    }

    /**
     * Mantener la pantalla encendida mientras haya cola.
     *
     * En un iPhone es lo que hace viable la estrategia entera: si la pantalla
     * se apaga, Safari suspende la pestaña y la subida se para. Se suelta en
     * cuanto la cola se vacía, porque quemar batería con la pantalla encendida
     * sin necesidad sería peor que el problema.
     */
    private async pedirWakeLock(): Promise<void> {
        const wl = (navigator as { wakeLock?: { request(t: 'screen'): Promise<WakeLockSentinel> } })
            .wakeLock;
        if (wl === undefined || this.wakeLock !== null) {
            return;
        }

        try {
            this.wakeLock = await wl.request('screen');
            this.wakeLock.addEventListener('release', () => {
                this.wakeLock = null;
            });
        } catch {
            // Lo deniega si la pestaña no está visible. No es un problema:
            // significa que ahora mismo no estamos en primer plano.
            this.wakeLock = null;
        }
    }

    private async soltarWakeLock(): Promise<void> {
        try {
            await this.wakeLock?.release();
        } catch {
            /* ya estaba suelto */
        }
        this.wakeLock = null;
    }

    private async avisar(): Promise<void> {
        const r = await this.resumen();
        for (const o of this.oyentes) {
            o(r);
        }
    }
}
